import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { scrollToId } from "@/lib/api";
import { CONTACT } from "@/lib/assets";

const LINKS = [
  { label: "Mission", href: "#mission", testid: "nav-link-mission" },
  { label: "Services", href: "#services", testid: "nav-link-services" },
  { label: "Inventaire", href: "#inventaire", testid: "nav-link-inventaire" },
  { label: "Recherche privée", href: "#recherche", testid: "nav-link-recherche" },
  { label: "Financement", href: "#financement", testid: "nav-link-financement" },
  { label: "Conciergerie", href: "#conciergerie", testid: "nav-link-conciergerie" },
  { label: "Tarifs", href: "#tarifs", testid: "nav-link-tarifs" },
  { label: "Flotte", href: "#flotte", testid: "nav-link-flotte" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-ink/85 backdrop-blur-xl border-b border-white/10 shadow-2xl" : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-[72px]">
        <button
          data-testid="nav-brand-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
        >
          <span className="w-9 h-9 rounded-full border border-gold/50 flex items-center justify-center font-display text-gold text-lg group-hover:bg-gold/10 transition-colors">
            G
          </span>
          <span className="text-left leading-tight">
            <span className="block text-[11px] tracking-[0.3em] uppercase text-slate-100 font-medium">
              Gestion Automobile
            </span>
            <span className="block text-[10px] tracking-[0.45em] uppercase text-gold-light/90">
              Exécutive Inc.
            </span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={l.testid}
              onClick={() => go(l.href)}
              className="text-[12px] uppercase tracking-[0.18em] text-slate-300 hover:text-gold-light transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <a
            data-testid="nav-phone-link"
            href={CONTACT.phoneHref}
            className="flex items-center gap-2 text-xs text-slate-300 hover:text-gold-light transition-colors whitespace-nowrap"
          >
            <Phone size={14} className="text-gold" />
            {CONTACT.phone}
          </a>
          <button
            data-testid="nav-cta-advisor"
            onClick={() => go("#contact")}
            className="btn-gold !px-6 !py-2.5 !text-[11px] whitespace-nowrap"
          >
            Parler à un conseiller
          </button>
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          className="lg:hidden text-slate-100 p-2"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-panel"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden bg-ink/95 backdrop-blur-xl border-b border-white/10 overflow-hidden"
          >
            <div className="container-x py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => go(l.href)}
                  className="text-left text-sm uppercase tracking-[0.2em] text-slate-200 hover:text-gold-light transition-colors py-1"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-cta-advisor-mobile"
                onClick={() => go("#contact")}
                className="btn-gold mt-3"
              >
                Parler à un conseiller
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
